import { useTheme } from "@hooks/useTheme";
import { ScanToolSearchInfoContext } from "@components/Guards/ScanToolGuard";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import Communication from "../../../components/Communication/Communication";
import ReadyIcon from "../../../components/Icons/ReadyIcon";
import ReviewIcon from "../../../components/Icons/ReviewIcon";
import { ComponentTable, ComponentTableHeader } from "../../../components/Tables";
import { BasicInfoSection, Breadcrumb, WorkflowProgressSbom } from "../../../components/UI";
import SbomComparisonResultModal from "../../../components/UI/SbomComparisonResultModal";
import queryKeys from "../../../configs/react-query/query-key";
import { useMutationData } from "../../../hooks/useMutationData";
import { useSbomVerification } from "../../../hooks/useSbomVerification";

const SbomVerification = () => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { projectSeq, sbomVerificationSeq } = useParams();
  const [modifiedComponentData, setModifiedComponentData] = useState([]);
  const [requests, setRequests] = useState([]);
  const [systemMessages, setSystemMessages] = useState([]);
  const { creatorList = [] } = useContext(ScanToolSearchInfoContext) ?? {};
  const queryClient = useQueryClient();

  // Comparison modal state
  const [showComparisonResultModal, setShowComparisonResultModal] = useState(false);
  const [selectedBinaryVerification, setSelectedBinaryVerification] = useState(null);

  // Set preference for SBOM tab when this component mounts
  useEffect(() => {
    // Safely set preference when component mounts
    try {
      localStorage.setItem("preferredTab", "sbom");
      localStorage.setItem("lastProjectSeq", projectSeq);
    } catch (e) {
      console.error("Error setting localStorage items:", e);
    }

    return () => {
      // This will run when component unmounts (including browser back)
      try {
        localStorage.setItem("preferredTab", "sbom");
        localStorage.setItem("lastProjectSeq", projectSeq);
      } catch (e) {
        console.error("Error setting localStorage items on unmount:", e);
      }
    };
  }, [projectSeq]);

  // Commented out sbom_action API call - requests and systemMessages will be empty arrays
  // const { data: requestList } = useFetchData(
  //   [queryKeys.sbomAction],
  //   "/sbom_action",
  //   {
  //     method: "GET",
  //     params: {
  //       verificationSeq: sbomVerificationSeq,
  //     },
  //   },
  //   {},
  //   {
  //     enabled: !!sbomVerificationSeq,
  //   }
  // );

  const completeAction = useMutationData(
    "/sbom_action_detail",
    {
      method: "POST",
      params: {
        actionType: "complete",
      },
    },
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries([queryKeys.sbomAction]);
      },
      onError: (error) => {
        console.error("Error completing action:", error);
      },
    },
  );

  const approveAction = useMutationData(
    "/sbom_action_detail",
    {
      method: "POST",
      params: {
        actionType: "approve",
      },
    },
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries([queryKeys.sbomAction]);
      },
      onError: (error) => {
        console.error("Error approving action:", error);
      },
    },
  );

  const rejectAction = useMutationData(
    "/sbom_action_detail",
    {
      method: "POST",
      params: {
        actionType: "reject",
      },
    },
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries([queryKeys.sbomAction]);
      },
      onError: (error) => {
        console.error("Error rejecting action:", error);
      },
    },
  );

  const createActionRequest = useMutationData(
    "/sbom_action_request",
    {
      method: "POST",
    },
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries([queryKeys.sbomAction]);
      },
      onError: (error) => {
        console.error("Error creating action request:", error);
      },
    },
  );

  const addVerificationComment = useMutationData(
    "/sbom_verification_verification_comment",
    {
      method: "PUT",
    },
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries([queryKeys.sbomAction]);
      },
      onError: (error) => {
        console.error("Error adding verification comment:", error);
      },
    },
  );

  const addFulfillmentComment = useMutationData(
    "/sbom_verification_fulfillment_comment",
    {
      method: "PUT",
    },
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries([queryKeys.sbomAction]);
      },
      onError: (error) => {
        console.error("Error adding fulfillment comment:", error);
      },
    },
  );

  const addUsageComment = useMutationData(
    "/sbom_verification_usage_comment",
    {
      method: "PUT",
    },
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries([queryKeys.sbomAction]);
      },
      onError: (error) => {
        console.error("Error adding usage comment:", error);
      },
      onMutate: (variables) => {
        return variables;
      },
    },
  );

  const {
    // Loading state
    isLoading,
    isFetching,

    // Data
    verificationStatus,
    hasSbomData,
    projectData,
    fetchedLicensePolicy,
    verificationData,
    fetchedSbomScanComponent,
    verificationComment,
    verificationFile,
    comparisonData,

    // Clarity verification comparison
    clarityVerifications,
    clarityVerificationsLoading,
    comparisonLoading,
    currentPage,
    itemsPerPage,
    totalPages,
    totalData,

    // Modal state
    showFileUploadModal,
    showRequestReVerifModal,
    showRequestModal,
    showCompleteVerifModal,
    showDecompleteVerifModal,
    showCompareModal,
    showDuplicateModal,
    hasErrors,

    // Modal controls
    setShowFileUploadModal,
    setShowRequestReVerifModal,
    setShowRequestModal,
    setShowCompleteVerifModal,
    setShowDecompleteVerifModal,
    setShowCompareModal,
    setVerificationComment,
    setVerificationFile,
    setRequestData,
    setHasErrors,
    setRepoInfo,

    // Table actions
    handleClarityVerificationPageChange,
    handleClarityVerificationItemsPerPageChange,
    selectClarityVerification,

    // Actions
    handleRequestVerificationClick,
    handleRequestVerification,
    handleCompleteVerificationClick,
    handleMoveToDone,
    handleDecompleteVerificationClick,
    handleDecompleteFromDone,
    handleCompareWithClarity,
    handleReUploadFile,
    handleConfirmFileUpload,
    handleRequestReVerification,
    handleDownloadComparisonResult,
    handleDownloadSbomVerificationResult,

    // Duplicate handling
    handleDuplicateModalClose,
    handleDuplicateConfirm,
  } = useSbomVerification(projectSeq, sbomVerificationSeq);


  const handleComponentDataChange = useCallback((data) => {
    if (data && data.length > 0) {
      setModifiedComponentData(data);
    }
  }, []);

  // Transform API data to Communication component format
  // useEffect(() => {
  //   if (requestList?.requests) {
  //     const transformedRequests = requestList.requests.map((request) => {
  //       // Find requester info from creatorList
  //       const requesterInfo = creatorList.find(user => user.value === request.createrId);
  //       const requesterRole = requesterInfo?.roleCdName || "unknown";

  //       // Find assignee info from creatorList
  //       const assigneeInfo = creatorList.find(user => user.value === request.assigneeId);
  //       const assigneeRole = assigneeInfo?.roleCdName || "unknown";

  //       return {
  //         id: request.actionSeq,
  //         date: new Date(request.createdAt).toLocaleString("en-US", {
  //           year: "numeric",
  //           month: "2-digit",
  //           day: "2-digit",
  //           weekday: "long",
  //           hour: "2-digit",
  //           minute: "2-digit",
  //           second: "2-digit",
  //         }).replace(/(\d+)\/(\d+)\/(\d+)/, '$3-$1-$2'),
  //         status: request.verificationStatusCdName || "Pending",
  //         actionStatus: request.actionStatus,
  //         requester: {
  //           name: request.createrName || request.createrId || "Unknown",
  //           role: requesterRole
  //         },
  //         assignee: {
  //           name: request.assigneeName || request.assigneeId || "Unknown",
  //           role: assigneeRole
  //         },
  //         description: request.actionsContent || "No description provided",
  //         completionRequestDate: request.completionRequestDt || request.createdAt,
  //         cve: request.cve,
  //         conversation: request.sbomActionDetails?.map((detail) => {
  //           // Find user info from creatorList
  //           const userInfo = creatorList.find(user => user.value === detail.createrId);
  //           const userRole = userInfo?.roleCdName || "Unknown";

  //           return {
  //             user: {
  //               name: detail.createrId || "Unknown",
  //               role: userRole
  //             },
  //             status: detail.actionStatus,
  //             timestamp: new Date(detail.createdAt).toLocaleString("en-US", {
  //           year: "numeric",
  //           month: "2-digit",
  //           day: "2-digit",
  //           weekday: "long",
  //           hour: "2-digit",
  //           minute: "2-digit",
  //           second: "2-digit",
  //         }).replace(/(\d+)\/(\d+)\/(\d+)/, '$3-$1-$2'),
  //             content: detail.actionContent || "No content provided",
  //           };
  //         }) || [],
  //       };
  //     });

  //     setRequests(transformedRequests);
  //   }

  //   // Process systemMessages
  //   if (requestList?.systemMessages) {
  //     setSystemMessages(requestList.systemMessages);
  //   }
  // }, [requestList, creatorList]);

  // Since we commented out the API call, initialize with empty arrays
  useEffect(() => {
    setRequests([]);
    setSystemMessages([]);
  }, []);

  // Get current step index based on verification status
  const getCurrentStepIndex = () => {
    const statusStepMap = {
      "Request ReVerification": 0,
      "Review Verification": 1,
      Done: 2,
    };
    return statusStepMap[verificationData.verificationStatusCdName] || 0;
  };

  // Workflow steps configuration
  const workflowSteps = [
    { label: "Request ReVerification", icon: null, displayLabel: t("fossid-status-options.reverification") }, // No icon for this step
    { label: "Review", icon: ReviewIcon, displayLabel: t("status.onReview") },
    { label: "Ready", icon: ReadyIcon, displayLabel: t("status.done") },
  ];

  // Helper function to get request data
  // const getRequestData = (requestId, actionContent) => {
  //   const request = requestList?.requests?.find(req => req.actionSeq === requestId);

  //   if (!request) {
  //     console.error("Request not found:", requestId);
  //     return null;
  //   }

  //   return {
  //     actionSeq: request.actionSeq,
  //     sbomVerificationSeq: sbomVerificationSeq,
  //     actionContent: actionContent
  //   };
  // };

  // Communication handlers
  const handleComplete = (completeData) => {
    const { requestId, message } = completeData;
    // const requestData = getRequestData(requestId, message);
    // if (requestData) {
    completeAction.mutate({ actionSeq: requestId, actionType: "complete", actionContent: message });
    // }
  };

  const handleApprove = (approvalData) => {
    const { requestId, message } = approvalData;
    // const requestData = getRequestData(requestId, message);
    // if (requestData) {
    approveAction.mutate({ actionSeq: requestId, actionType: "approve", actionContent: message });
    // }
  };

  const handleReject = (rejectionData) => {
    const { requestId, message } = rejectionData;
    // const requestData = getRequestData(requestId, message);
    // if (requestData) {
    rejectAction.mutate({ actionSeq: requestId, actionType: "reject", actionContent: message });
    // }
  };

  const handleAddRequest = (requestData) => {
    // Convert date to ISO format
    const completionRequestDt = new Date(requestData.completionRequestDate).toISOString();

    const requestPayload = {
      assigneeId: requestData.assignee,
      completionRequestDt: completionRequestDt,
      actionsContent: requestData.requestMessage,
      sbomVerificationSeq: sbomVerificationSeq,
      ...(requestData.cve && { cve: requestData.cve }),
    };

    createActionRequest.mutate(requestPayload);
  };

  const handleAddComment = (commentData) => {
    // Check verification status to determine which API to call
    if (verificationData?.verificationStatusCd === "STANDBY_FOLLOWUP") {
      addFulfillmentComment.mutate(commentData);
    } else if (verificationData?.verificationStatusCd === "ON_REVIEW") {
      addVerificationComment.mutate(commentData);
    } else if (
      verificationData?.verificationStatusCd === "ON_VERIFICATION" &&
      verificationData?.verificationStatusCdName === "Usage Type Check"
    ) {
      addUsageComment.mutate(commentData);
    } else {
    }
  };

  // Normalize backend-provided arrays like "[\"1 : sha1\", ...]" to ["sha1", ...]
  const parseListFromBackend = (raw) => {
    if (!raw) return [];
    let arr = [];
    if (Array.isArray(raw)) {
      arr = raw;
    } else if (typeof raw === "string") {
      try {
        arr = JSON.parse(raw);
      } catch (e) {
        arr = [];
      }
    }
    return arr.map((item) => {
      const text = String(item);
      const colonIdx = text.indexOf(":");
      return colonIdx >= 0 ? text.slice(colonIdx + 1).trim() : text;
    });
  };

  const parseListRaw = (raw) => {
    if (!raw) return [];
    let arr = [];
    if (Array.isArray(raw)) {
      arr = raw;
    } else if (typeof raw === "string") {
      try {
        arr = JSON.parse(raw);
      } catch (e) {
        arr = [];
      }
    }
    return arr.map((item) => String(item));
  };

  // Handle clarity verification selection from the first modal
  const handleSelectClarityVerification = (verification) => {
    setSelectedBinaryVerification(verification);
    setShowCompareModal(false); // Close the first modal
    // Small delay to ensure smooth transition
    setTimeout(() => {
      setShowComparisonResultModal(true); // Show the comparison result modal
    }, 200);
  };

  // Handle going back to select another verification
  const handleSelectOtherVerification = () => {
    setShowComparisonResultModal(false); // Close the comparison result modal
    // Small delay to ensure smooth transition
    setTimeout(() => {
      setShowCompareModal(true); // Show the first modal again
      setSelectedBinaryVerification(null);
    }, 200);
  };

  // Handle download comparison result
  const handleDownloadComparisonResultClick = () => {
    if (selectedBinaryVerification) {
      handleDownloadComparisonResult();
    }
  };

  return (
    <div className="min-h-full" data-testid="sbom-verification-page">
      {/* Header with Breadcrumbs */}
      <div className="p-4" data-testid="sbom-verification-header">
        <div className="space-y-3">
          <Breadcrumb
            data-testid="sbom-verification-breadcrumb"
            onBackClick={() => {
              navigate(`/projects/${projectSeq}`, { state: { activeTab: "sbom" } });
            }}
            items={[
              { label: t("navbar.project-list"), href: "/projects" },
              {
                label: t("navbar.project-details"),
                onClick: () => {
                  navigate(`/projects/${projectSeq}`, { state: { activeTab: "sbom" } });
                },
              },
              { label: t("navbar.sbom-verification") },
            ]}
          />
          <div>
            <h1
              className={`text-2xl font-bold ${colors.textPrimary}`}
              data-testid="sbom-verification-page-title"
            >
              {verificationData.realFileName}
            </h1>
          </div>
        </div>
      </div>

      <div>
        {/* Basic Information */}
        <BasicInfoSection
          data-testid="sbom-verification-basic-info"
          data={{
            ...verificationData,
            projectName: projectData?.projectName || verificationData?.projectName,
          }}
          colors={colors}
          t={t}
          type={"sbom"}
        />

        {/* Workflow Progress */}
        <div className={`px-6 py-6 rounded-2xl bg-[${colors.bgPrimaryColor}]`} data-testid="sbom-verification-workflow">
          <WorkflowProgressSbom
            steps={workflowSteps}
            currentStep={getCurrentStepIndex()}
            projectManagerId={projectData?.projectManagerId}
            onRequestReVerification={handleRequestReVerification}
            onMoveToDone={handleMoveToDone}
            onDecompleteFromDone={handleDecompleteFromDone}
            onCompareWithClarity={handleCompareWithClarity}
            // Props for clarity comparison
            showCompareModal={showCompareModal}
            onSelectClarityVerification={handleSelectClarityVerification}
            selectedBinaryVerification={selectedBinaryVerification}
            onDownloadComparisonResult={handleDownloadComparisonResultClick}
            sbomVerificationData={verificationData}
            projectData={projectData}
            isLoading={isLoading}
            isFetching={isFetching}
            sbomVerificationSeq={sbomVerificationSeq}
          />
        </div>
      </div>
      {/* Main Content */}
      <div className="space-y-6 mt-6">
        {/* Communication Section */}
        <div className={`mt-5 p-6 ${colors.bgPrimary} rounded-2xl`} data-testid="sbom-verification-communication">
          <Communication
            requests={requests}
            systemMessages={systemMessages}
            verificationData={verificationData}
            verificationType="sbom"
            verificationSeq={sbomVerificationSeq}
            onComplete={handleComplete}
            onApprove={handleApprove}
            onReject={handleReject}
            onAddRequest={handleAddRequest}
            onAddComment={handleAddComment}
            isAddCommentPending={
              addVerificationComment.isPending || addFulfillmentComment.isPending || addUsageComment.isPending
            }
            projectSeq={projectSeq}
            projectData={projectData}
            creatorList={creatorList}
          />
        </div>

        {/* Component List */}
        <div data-testid="sbom-verification-component-table">
          <ComponentTable
            data={fetchedSbomScanComponent}
            licensePolicyData={fetchedLicensePolicy}
            onDataChange={handleComponentDataChange}
            seq="sbomScanComponentSeq"
            className="p-4"
            allowVexReview
            showShieldFilter
            vexContext={{
              scanToolCd: "sbom",
              verificationSeq: sbomVerificationSeq,
              projectSeq,
              projectManagerId: projectData?.projectManagerId,
              verificationStatusCd: verificationData?.verificationStatusCd,
            }}
          >
            <ComponentTableHeader
              title={t("common.component-list")}
              count={fetchedSbomScanComponent?.length || 0}
              isEditMode={false}
              isEditingAllowed={false}
              t={t}
              colors={colors}
              sbomMissings={parseListRaw(verificationData?.missings)}
              sbomNotApplicables={parseListRaw(verificationData?.notApplicables)}
              sbomScanComponents={fetchedSbomScanComponent}
            />
          </ComponentTable>
        </div>
      </div>

      {/* Comparison Result Modal */}
      <SbomComparisonResultModal
        show={showComparisonResultModal}
        onHide={() => setShowComparisonResultModal(false)}
        sbomVerificationSeq={sbomVerificationSeq}
        binaryVerificationSeq={selectedBinaryVerification?.binaryVerificationSeq}
        onSelectOtherVerification={handleSelectOtherVerification}
        onDownloadComparisonResult={handleDownloadComparisonResultClick}
        licensePolicyData={fetchedLicensePolicy}
      />
    </div>
  );
};

export default SbomVerification;
